import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CalendarPlus,
  CalendarX,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  Undo2,
  X,
} from "lucide-react";
import { useTRPC } from "@/trpc";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { format, formatDistanceToNow, isPast, addMinutes } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ProposalSummary = {
  id: string;
  status: string;
  initiatedAt: Date | string;
  initiatedByUserId: string;
  initiatedByRole: string;
  expiresAt: Date | string | null;
  scheduleCount: number;
  shiftCancelCount: number;
};

const statusConfig: Record<string, { label: string; className: string }> = {
  pending: {
    label: "Pending",
    className: "bg-amber-100 text-amber-700",
  },
  accepted: {
    label: "Accepted",
    className: "bg-emerald-100 text-emerald-700",
  },
  declined: {
    label: "Declined",
    className: "bg-red-100 text-red-700",
  },
  withdrawn: {
    label: "Withdrawn",
    className: "bg-slate-100 text-slate-600",
  },
  expired: {
    label: "Expired",
    className: "bg-slate-100 text-slate-600",
  },
};

export function ProposalListItem({
  proposal,
  jobOfferId,
  defaultExpanded = false,
  role = "organization",
  canRespond = false,
}: {
  proposal: ProposalSummary;
  jobOfferId: string;
  defaultExpanded?: boolean;
  role?: "organization" | "provider";
  canRespond?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const trpc = useTRPC();

  const { data: details, isLoading } = useQuery({
    ...trpc.jobOffer.getProposalDetails.queryOptions({
      proposalId: proposal.id,
    }),
    enabled: expanded,
    refetchInterval: (query) =>
      query.state.data?.status === "pending" ? 30_000 : false,
  });

  const invalidateAfterAction = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.jobOffer.getProposalDetails.queryKey({
          proposalId: proposal.id,
        }),
      }),
      ...(role === "organization"
        ? [
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.organizationGetAll.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.organizationGet.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.orgGet.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.orgListPendingProposals.queryKey(),
            }),
          ]
        : [
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.providerListAccepted.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.providerListOpen.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.providerListPendingProposals.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.jobOffer.getForCard.queryKey({
                id: jobOfferId,
              }),
            }),
          ]),
    ]);
  };

  const { mutateAsync: withdraw, isPending: isWithdrawing } = useMutation(
    trpc.jobOffer.withdrawProposal.mutationOptions({
      onSuccess: async () => {
        toast.success("Proposal withdrawn");
        await invalidateAfterAction();
      },
    }),
  );

  const { mutateAsync: respond, isPending: isResponding } = useMutation(
    trpc.jobOffer.respondToProposal.mutationOptions({
      onSuccess: async (_data, variables) => {
        toast.success(
          variables.accepted ? "Proposal accepted" : "Proposal declined",
        );
        await invalidateAfterAction();
      },
    }),
  );

  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showRespondDialog, setShowRespondDialog] = useState<
    "accept" | "decline" | null
  >(null);

  // Can withdraw if you're on the same side as the initiator
  const isMyProposal = proposal.initiatedByRole === role;

  const status = statusConfig[proposal.status] ?? statusConfig.pending;
  const isPending = proposal.status === "pending";
  const initiatedAt =
    typeof proposal.initiatedAt === "string"
      ? new Date(proposal.initiatedAt)
      : proposal.initiatedAt;

  const summaryParts: string[] = [];
  if (proposal.scheduleCount > 0) {
    summaryParts.push(
      `${proposal.scheduleCount} date${proposal.scheduleCount !== 1 ? "s" : ""} to add`,
    );
  }
  if (proposal.shiftCancelCount > 0) {
    summaryParts.push(
      `${proposal.shiftCancelCount} shift${proposal.shiftCancelCount !== 1 ? "s" : ""} to cancel`,
    );
  }

  return (
    <>
      <div className="rounded-lg border border-gray-100 bg-gray-50 overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-100 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">
                {isMyProposal
                  ? "Your proposal"
                  : role === "organization"
                    ? "Provider proposal"
                    : "Facility proposal"}
              </span>
              <Badge
                className={`${status.className} border-0 text-xs font-medium`}
              >
                {status.label}
              </Badge>
              <span className="text-xs text-gray-400">
                {format(initiatedAt, "MMM d")}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {summaryParts.join(" · ")}
            </p>
          </div>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
          )}
        </button>

        {expanded && (
          <div className="border-t border-gray-100 px-3 py-3 space-y-3">
            {isLoading && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            )}

            {details && (
              <>
                {details.initiatedNote && (
                  <p className="text-sm text-gray-600 italic">
                    &ldquo;{details.initiatedNote}&rdquo;
                  </p>
                )}

                {isPending && details.expiresAt && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      {isPast(new Date(details.expiresAt))
                        ? "Expired"
                        : `Expires ${formatDistanceToNow(new Date(details.expiresAt), { addSuffix: true })}`}
                    </span>
                  </div>
                )}

                {details.schedules.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Dates to Add
                    </p>
                    {details.schedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <CalendarPlus className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span>
                          {format(new Date(schedule.startsAt), "MMM d, yyyy")}{" "}
                          &middot;{" "}
                          {format(new Date(schedule.startsAt), "h:mm a")}
                          {" - "}
                          {format(
                            addMinutes(
                              new Date(schedule.startsAt),
                              schedule.durationMinutes,
                            ),
                            "h:mm a",
                          )}
                          {schedule.rrule && " (recurring)"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {details.shiftCancels.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Shifts to Cancel
                    </p>
                    {details.shiftCancels.map((cancel) => (
                      <div
                        key={cancel.shiftId}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <CalendarX className="h-3.5 w-3.5 text-rose-500 shrink-0" />
                        <span>
                          {format(
                            new Date(cancel.shift.startsAt),
                            "MMM d, yyyy",
                          )}{" "}
                          &middot;{" "}
                          {format(new Date(cancel.shift.startsAt), "h:mm a")}{" "}
                          &ndash;{" "}
                          {format(new Date(cancel.shift.endsAt), "h:mm a")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {details.respondedNote && (
                  <p className="text-sm text-gray-500 italic border-t border-gray-100 pt-2">
                    Response: &ldquo;{details.respondedNote}&rdquo;
                  </p>
                )}

                {isPending && (
                  <div className="pt-1 flex gap-2">
                    {isMyProposal ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-gray-600"
                        onClick={() => setShowWithdrawDialog(true)}
                        disabled={isWithdrawing}
                      >
                        {isWithdrawing ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Withdraw
                      </Button>
                    ) : !canRespond ? (
                      <p className="text-sm text-gray-500 italic">
                        You don't have permission to respond to proposals.
                      </p>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => setShowRespondDialog("accept")}
                          disabled={isResponding}
                        >
                          {isResponding ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          ) : (
                            <Check className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-rose-600 border-rose-200 hover:bg-rose-50"
                          onClick={() => setShowRespondDialog("decline")}
                          disabled={isResponding}
                        >
                          <X className="h-3.5 w-3.5 mr-1.5" />
                          Decline
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <AlertDialog
        open={showWithdrawDialog}
        onOpenChange={setShowWithdrawDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this proposal?</AlertDialogTitle>
            <AlertDialogDescription>
              {role === "organization"
                ? "The provider will be notified that you withdrew the proposed changes."
                : "The facility will be notified that you withdrew the proposed changes."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => withdraw({ proposalId: proposal.id })}
            >
              Withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showRespondDialog !== null}
        onOpenChange={(open) => !open && setShowRespondDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {showRespondDialog === "accept"
                ? "Accept this proposal?"
                : "Decline this proposal?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {showRespondDialog === "accept"
                ? "The proposed schedule changes will be applied to this job offer."
                : role === "organization"
                  ? "The provider will be notified that you declined the proposed changes."
                  : "The facility will be notified that you declined the proposed changes."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                showRespondDialog === "decline"
                  ? "bg-rose-600 hover:bg-rose-700"
                  : ""
              }
              onClick={() =>
                respond({
                  proposalId: proposal.id,
                  accepted: showRespondDialog === "accept",
                })
              }
            >
              {showRespondDialog === "accept" ? "Accept" : "Decline"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
